import { ITableRow, ITableViewRowLock, ITableViewColumn } from 'types'
import { doesFilterMatchValue } from 'components/spreadsheet/helpers/filtering'
import { IColumnValuesCount } from '../types'
import { getRowValue } from './functions'
import { CompressedColumnStore } from 'components/spreadsheet/helpers/compressed-column-store'
import { recordListsEqual } from 'components/spreadsheet/helpers/record-equality'

export interface IRowLockState {
  rowLocked: boolean
  lockedColumnIds: Set<string>
}

// Value-based comparison of two lists of row-lock rules, insensitive to object
// key order and to whether the optional value / multipleValues fields are present.
// The backend RowLockSchema strips whichever field doesn't apply (an all_values
// rule has no value), but the lock menu rebuilds objects that add `value: ''`;
// comparing those with JSON.stringify lit the save icon on merely opening the
// lock menu on a view with existing rules (#10120). recordListsEqual compares the
// union of keys, so a new attribute added to ITableViewRowLock is picked up
// automatically without editing this helper.
export const areRowLockSettingsEqual = (a: ITableViewRowLock[], b: ITableViewRowLock[]): boolean => {
  return recordListsEqual(a as unknown as Record<string, unknown>[], b as unknown as Record<string, unknown>[])
}

/**
 * Evaluate row-lock rules against a single row.
 *
 * - Rules with `applyToEntireRow=true` flip `rowLocked` to true on the first match.
 * - Rules with `applyToEntireRow=false` add their criteria column to `lockedColumnIds`.
 *
 * Where a rule is evaluated depends on whether its criteria column's value was streamed to this
 * viewer (tracked by `columnIdToIndex` — a column can be configured on the view yet not streamed,
 * e.g. a personal view in a process appends hidden parent columns to the view's column list):
 * - **Value streamed → here, on the client.** The cell value is already loaded, and evaluating
 *   locally is what lets a lock update instantly as the user edits (why locks are client-side —
 *   #10066), without a server round-trip.
 * - **Value not streamed (hidden) → on the server.** Its values must never reach the client (a
 *   privacy leak), and a hidden column can't be edited in the view anyway, so its match can't
 *   change client-side. The backend evaluates such rules and streams a single per-row locked flag
 *   (`row.hiddenRowLocked`, see `stream_view_data`); here we just honour that flag.
 *
 * Mirrors `getRowLevelColour` so the predicate plumbing stays consistent
 * between colour and lock evaluation.
 */
export const getRowLockState = (
  rowLockSettings: ITableViewRowLock[],
  row: ITableRow,
  columnLookup: Map<string, ITableViewColumn>,
  columnIdToIndex: Map<string, number>,
  columnValuesCount: IColumnValuesCount,
  columnStore?: CompressedColumnStore | null
): IRowLockState => {
  let rowLocked = false
  const lockedColumnIds = new Set<string>()

  for (let i = 0; i < rowLockSettings.length; i++) {
    const rowLockSetting = rowLockSettings[i]
    // A rule is evaluable on the client only if its criteria column's value was actually streamed
    // to this viewer. Gating on columnLookup (the view's configured columns) is not enough: a
    // personal view in a process appends hidden parent columns to that list even though they were
    // never streamed, so an absent value would be read as empty and the server flag ignored.
    const isColumnStreamed = columnIdToIndex.has(rowLockSetting.columnId)
    const ruleColumn = isColumnStreamed ? columnLookup.get(rowLockSetting.columnId) : undefined
    if (!ruleColumn) {
      if (rowLockSetting.applyToEntireRow && row.hiddenRowLocked) {
        rowLocked = true
      }
      continue
    }

    const ruleColumnValue = getRowValue(row, rowLockSetting.columnId, columnIdToIndex, columnStore)
    // RowLockSchema strips `value` / `multipleValues` for filter types that
    // don't use them, so default at the boundary; `doesFilterMatchValue`
    // takes the unwrapped primitives.
    const match = doesFilterMatchValue(
      rowLockSetting.filterType,
      rowLockSetting.value ?? '',
      rowLockSetting.multipleValues ?? [],
      ruleColumnValue,
      '',
      '',
      [],
      [],
      [],
      [],
      ruleColumn,
      columnValuesCount,
      row
    )
    if (!match) continue

    if (rowLockSetting.applyToEntireRow) {
      rowLocked = true
    } else {
      lockedColumnIds.add(rowLockSetting.columnId)
    }
  }

  return { rowLocked, lockedColumnIds }
}
